import React, { useEffect, useState, useRef } from 'react';
import qs from 'qs';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { baseConfig } from '../../../config/algolia';
import { buildSearchFilters } from '@jbhi-fi/utils/src/buildAlgoliaSearchFilters/buildAlgoliaSearchFilters';
import { Index, Configure, InstantSearch, useSearchBox, useInstantSearch } from 'react-instantsearch';
import { CustomStats } from '../refinements/CustomStats';
import CustomInfiniteHits from './CustomInfiniteHits';
import { RefinementContainer } from '../refinements/RefinementContainer';
import he from 'he';
import useSearchRedirectURL from '../../../components/search/useSearchRedirectURL';
import { ProductClientProvider } from '@jbhi-fi/components/src/context/ProductClientProvider';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ShopifyProductProvider } from '../../../api/shopify/ShopifyProduct';
import { productClient, ProductClient } from '../../../api/algolia/ProductClient';
import {
  urlToSearchState,
  queryStateToUrl,
  SearchState,
  type AlgoliaQueryState,
} from '../collections/searchStateUrlStorageHelpers';
import { getAnalyticsTags } from '../../../utils/algolia/search-insights';
import { InsightsMiddleware } from '../../../utils/algolia/InsightsMiddleware';
import { SearchContentContainer } from '../../../components/searchcontent/SearchContentContainer';

type Facet = {
  facet: string;
  type: string;
  title?: string;
};

type PriceRange = {
  min: number;
  max: number;
};

/**
 * The gtm context provider
 */
import GtmContext from '../../../contexts/GtmContext';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag/useFeatureFlag';
import CustomRefinementListContainer from '../refinements/CustomRefinementListContainer';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { styles } from './ResultsContainer.css';

const { app_id = '', base_facets, product_families_api_key = '', product_families_index } = baseConfig;

/**
 * Initialise the algolia search client and the index
 */
const algoliaSearchClient = algoliasearch(app_id, product_families_api_key, ProductClient.getAlogliaCache(app_id));

const INDEX_NAME = product_families_index!;
const DEBOUNCE_TIME = 400;

/**
 * Page Title
 */
const PageTitleWithRedirect = () => {
  const { results: searchResults, status } = useInstantSearch();
  const hasRedirect = useSearchRedirectURL(searchResults);
  const nbHits = searchResults?.nbHits ?? 0;
  const query = searchResults?.query;
  const isSearching = status === 'loading' || status === 'stalled';
  const isInitialLoad = searchResults?.__isArtificial;

  if ((!nbHits && !query) || hasRedirect || (!nbHits && isSearching) || isInitialLoad) {
    return (
      <div
        style={{ width: '100%', maxWidth: '400px', height: '40px', backgroundColor: '#EEEEEE', margin: '0 0 17.5px 0' }}
      />
    );
  }

  return <h3 className="search-title">{`${nbHits} result${nbHits === 1 ? '' : 's'} for "${query}"`}</h3>;
};

const VirtualSearchBox = (props: any) => {
  useSearchBox(props);
  return null;
};

/**
 *  Get Base Search Facets
 *  Generate the base search facets on initial render.
 *  We check against the initial SearchState to ensure any refinementList
 *  values are included in the refinementFacets.
 */
type GetBaseSearchFacets = {
  baseFacets: Array<Facet>;
  searchState: SearchState;
};

const getBaseSearchFacets = ({ baseFacets, searchState }: GetBaseSearchFacets) => {
  const baseSearchFacets = [
    ...baseFacets,
    {
      facet: 'Category',
      type: 'refinementList',
    },
    {
      facet: 'Artist',
      type: 'refinementList',
    },
  ];

  if (!searchState.refinementList) return baseSearchFacets;

  Object.keys(searchState.refinementList).forEach((key: string) => {
    baseSearchFacets.push({
      facet: key.replace(/^[^.]+\./, ''),
      type: 'refinementList',
    });
  });

  return baseSearchFacets;
};

/**
 *  Get Search Query Facets
 *  Generate the search query facets based on the returned results and
 *  the base search facets.
 */
type GetSearchQueryFacets = {
  results: any;
  baseSearchFacets: Array<Facet>;
};

const getSearchQueryFacets = ({ results, baseSearchFacets }: GetSearchQueryFacets) => {
  const facets = results?.renderingContent?.facetOrdering?.facets?.order;
  const searchQueryFacets = [...baseSearchFacets].filter(
    (facet, index, self) => index === self.findIndex(t => t.facet === facet.facet)
  );

  if (!facets) return searchQueryFacets;

  facets.forEach((facet: string) => {
    let facetName = facet.replace(/^[^.]+\./, '');
    if (facetName === 'brand') facetName = 'Brand';
    // Temp case sensitivity fix for the "brand" facet as some of the facet values are not capitalized in the algolia

    if (searchQueryFacets.some(baseFacet => baseFacet.facet === facetName)) return;

    let type = 'refinementList';
    if (facetName.toLowerCase() === 'price') {
      type = 'priceRanges';
    } else if (facetName.toLowerCase() === 'ismarketplace') {
      type = 'toggleRefinement';
    }

    searchQueryFacets.push({
      facet: facetName,
      type,
    });
  });

  return searchQueryFacets;
};

interface SearchResultsProps {
  searchState: SearchState;
  priceRange?: PriceRange;
  clearanceItems?: any | null;
  nbSaleItems: number;
  setNbSaleItems: (nbSaleItems: number) => void;
  nbClearanceItems: number;
  setNbClearanceItems: (nbClearanceItems: number) => void;
  getTerm: string;
}

const SearchResults = ({
  searchState,
  priceRange,
  nbSaleItems,
  setNbSaleItems,
  nbClearanceItems,
  setNbClearanceItems,
  getTerm,
}: SearchResultsProps) => {
  const { results } = useInstantSearch();
  const isDesktop = useBreakpoint('lg', 'up');

  const baseSearchFacets = getBaseSearchFacets({
    baseFacets: base_facets,
    searchState,
  });

  const [refinementFacets, setRefinementFacets] = useState([...baseSearchFacets]);

  useEffect(() => {
    const searchQueryFacets = getSearchQueryFacets({
      results,
      baseSearchFacets,
    });

    if (!searchQueryFacets.length) return;

    setRefinementFacets(searchQueryFacets);
  }, [JSON.stringify(results.disjunctiveFacets, null, 0)]);

  return (
    <>
      <Index indexId="saleitems" indexName={INDEX_NAME}>
        <Configure filters={buildSearchFilters(`onPromotion:true`)} query={getTerm} distinct />
        <CustomStats setNbItems={setNbSaleItems} nbItems={nbSaleItems} />
      </Index>

      <Index indexId="clearance" indexName={INDEX_NAME}>
        <Configure filters={buildSearchFilters(`banner_tags.label:Clearance`)} query={getTerm} distinct />
        <CustomStats setNbItems={setNbClearanceItems} nbItems={nbClearanceItems} />
      </Index>

      <PageTitleWithRedirect />
      <SearchContentContainer term={getTerm} />
      <VirtualSearchBox query={searchState.query} />
      <RefinementContainer
        facets={refinementFacets}
        indexName={INDEX_NAME}
        priceRange={priceRange}
        nbSaleItems={nbSaleItems}
        nbClearanceItems={nbClearanceItems}
        isSearchPage={true}
        shouldHandleRedirects
        isDesktop={isDesktop}
      />
      <div className={`${styles.resultsContainer} search-refinement-container`}>
        {isDesktop ? (
          <div className={`${styles.resultsRefinementContainer}`}>
            <CustomRefinementListContainer
              initialFacets={refinementFacets}
              nbHits={results?.nbHits}
              priceRange={priceRange ?? { min: 0, max: 0 }}
              mobileShow={false}
              setMobileShowFilter={() => undefined}
              nbSaleItems={nbSaleItems}
              trackingInfo={{ defaults: { hitsPerPage: 36, sortBy: 'relevancy' } }}
              isSearchPage={true}
              isSticky={false}
              nbClearanceItems={nbClearanceItems}
            />
          </div>
        ) : null}
        <div className={styles.searchResultsContent} data-search-results-content>
          <CustomInfiniteHits />
        </div>
      </div>
    </>
  );
};

interface ResultsContainerProps {
  term?: string;
  priceRange?: PriceRange;
}

const ResultsContainer = ({ term, priceRange }: ResultsContainerProps) => {
  const query = window.location.search ? qs.parse(window.location.search.slice(1)).query : null;
  const getTerm: any = term ? term : query ? query : '';
  const [nbSaleItems, setNbSaleItems] = useState(0);
  const [nbClearanceItems, setNbClearanceItems] = useState(0);
  const debouncedSetStateRef = useRef<number | undefined>(undefined);
  const [searchState, setSearchState] = useState(urlToSearchState(window.location, null, true));
  const [gtm, setGtm] = useState({
    group: 'site search',
    category: 'search',
    search: {
      term: getTerm,
    },
  });
  const algoliaPersonalizationImpact = useFeatureFlag('algoliaPersonalizationImpact') ?? 0;

  useEffect(() => {
    setSearchState(urlToSearchState(window.location, null, true));
  }, [window.location]);

  useEffect(() => {
    // Add search term to skeleton search box in header and document title
    const searchBoxElement: HTMLInputElement = document.querySelector(
      '#quicksearch-search-box input'
    ) as HTMLInputElement;
    if (getTerm) {
      searchBoxElement.value = getTerm;
      document.title = `Search results: '${getTerm}' | JB Hi-Fi`;
    } else {
      searchBoxElement.click();
    }

    window.addEventListener('popstate', () => {
      if (window.location.pathname !== '/search' && window.location.pathname !== '/search/') {
        window.location.reload();
      }
    });

    // Add breadcrumbs
    if (
      query &&
      (!document.querySelector('.breadcrumbs-container') ||
        (document.querySelector('.breadcrumbs-container') &&
          document.querySelector('.breadcrumbs-container')?.getClientRects().length === 0))
    ) {
      const el = document.createElement('div');
      el.className = 'breadcrumbs-container';
      el.dataset.nosnippet = '';
      el.innerHTML = `<ol class="breadcrumbnav">
        <li>
          <a href="/">
            Home
          </a>
        </li>
        <li>
          Search results: '${he.encode(getTerm)}'
        </li>
      </ol>`;

      const nextSibling = document?.querySelector('#shopify-section-header')?.nextSibling;
      if (nextSibling) {
        document?.querySelector('#shopify-section-header')?.parentNode?.insertBefore(el, nextSibling);
      }
    }

    return () => clearTimeout(debouncedSetStateRef.current);
  }, []);

  /**
   * URL sync to have the URL reflect the state of the search.
   *
   * https://www.algolia.com/doc/guides/building-search-ui/going-further/routing-urls/react/#basic-urls
   */
  const onSearchStateChange = (updatedSearchStates: any) => {
    clearTimeout(debouncedSetStateRef.current);
    const updatedSearchState = updatedSearchStates[INDEX_NAME];

    /**
     * Filter out only the necessary parameters to sync to the URL.
     */
    const newQueryState: AlgoliaQueryState = {
      query: updatedSearchState.query,
      refinementList: updatedSearchState.refinementList,
      range: updatedSearchState.range,
      hitsPerPage: updatedSearchState.hitsPerPage,
      sortBy: updatedSearchState.sortBy,
      toggle: updatedSearchState.toggle,
    };

    debouncedSetStateRef.current = window.setTimeout(() => {
      window.history.pushState(updatedSearchState, '', queryStateToUrl(window.location, newQueryState, true));
    }, DEBOUNCE_TIME);
    setSearchState(updatedSearchState);
  };

  if (!query) {
    return <></>;
  }

  return (
    <GtmContext.Provider value={{ gtm, setGtm }}>
      <QueryClientProvider client={new QueryClient()}>
        <ProductClientProvider client={productClient}>
          <ShopifyProductProvider>
            <InstantSearch
              key={INDEX_NAME}
              searchClient={algoliaSearchClient}
              indexName={INDEX_NAME}
              initialUiState={{
                [INDEX_NAME]: searchState,
              }}
              future={{
                persistHierarchicalRootCount: false,
                preserveSharedStateOnUnmount: true,
              }}
              onStateChange={({ uiState, setUiState }) => {
                onSearchStateChange(uiState);
                setUiState(uiState);
              }}
            >
              <InsightsMiddleware />
              <Configure
                analyticsTags={getAnalyticsTags('search')}
                clickAnalytics
                filters={buildSearchFilters()}
                query={getTerm as string}
                distinct
                {...(algoliaPersonalizationImpact > 0
                  ? {
                      personalizationImpact: algoliaPersonalizationImpact,
                      enablePersonalization: true,
                    }
                  : {})}
              />
              <SearchResults
                searchState={searchState}
                priceRange={priceRange}
                nbSaleItems={nbSaleItems}
                setNbSaleItems={setNbSaleItems}
                nbClearanceItems={nbClearanceItems}
                setNbClearanceItems={setNbClearanceItems}
                getTerm={getTerm}
              />
            </InstantSearch>
          </ShopifyProductProvider>
        </ProductClientProvider>
      </QueryClientProvider>
    </GtmContext.Provider>
  );
};

export default ResultsContainer;
